import { getCurrentScope, getReplay, setTag, SeverityLevel, withScope } from "@sentry/react"

import { isProd } from "../env"
import { logger } from "./logging"

export type SubComponent = "client" | "admin"

export function registerSessionToEvents(params: { id: string; customerId: string; customerName: string }) {
  const { id, customerId, customerName } = params
  getCurrentScope().setTags({
    session_id: id,
    customer_id: customerId,
    customer_name: customerName,
    locale: navigator.language,
  })
}

export function captureException(exception: unknown, extra?: Record<string, string | number | boolean>) {
  try {
    withScope((scope) => {
      if (extra) {
        scope.setExtras(extra)
      }
      scope.captureException(exception)
    })
  } catch (error) {
    logger.error(error)
  }
}

export function captureExceptionWithoutAlert(
  exception: unknown,
  extra?: Record<string, string | number | boolean>,
  fingerprint?: string[],
) {
  try {
    withScope((scope) => {
      scope.setTag("no-alert", true)
      scope.setExtras({ ...extra })

      // Groups the exception on its own instead of by stacktrace, which merges unrelated tRPC errors
      if (fingerprint) {
        scope.setFingerprint(fingerprint)
      }

      scope.captureException(exception)
    })
  } catch (error) {
    logger.error(error)
  }
}

type CaptureSentryMessageParams = {
  message: string
  level: SeverityLevel
  extra?: Record<string, string | number | boolean>
}

export function captureSentryMessage(params: CaptureSentryMessageParams) {
  const { message, extra, level } = params

  try {
    withScope((scope) => {
      scope.setExtras({ ...extra })
      scope.setLevel(level)

      scope.captureMessage(message)
    })
  } catch (error) {
    logger.error(error)
  }
}

type FullSessionReplayReason = "customer" | "iosImageCapture"

/** Records the whole session instead of the error-only buffer, the reason tag tells why the session is recorded. */
export function startFullSessionReplay(reason: FullSessionReplayReason) {
  setTag("replayReason", reason)

  // skip when replay is not set up or already records
  const replay = getReplay()
  if (!isProd() || !replay || replay.getRecordingMode() === "session") {
    return
  }

  // Turns the error-only buffer into a full session recording, or starts a new one when nothing records yet
  replay.flush().catch((error) => logger.warn("Sentry replay flush failed", error))
}
